/**
 * AI CONTEXT
 * Purpose: grouped task card per student/topic with urgency, checklist, and complete toggle.
 * Writes: updates student.tasks array via save hook.
 * Used by: StudentTaskList.
 * Importance: operational follow-up tracking for student actions.
 * Refactor: none — task toggle lives in useToggleStudentTask.
 */
import { ClockCircleOutlined } from "@ant-design/icons"
import { Button, Checkbox, Tooltip } from "antd"
import { motion, type TargetAndTransition } from "motion/react"
import { useEffect, useState } from "react"
import styled from "styled-components"

import Card from "#blocks/Card"
import Flex from "#blocks/Flex"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  StudentTask,
  StudentTaskCardData,
} from "#customTypes/composedTypes/studentTaskTypes"
import { type ReactChild } from "#customTypes/globalTypes"
import { useToggleStudentTask } from "#hooks/newHooks/useToggleStudentTask"
import { COLORS } from "#styles/colors"
import {
  getPresentHighlightPhase,
  PRESENT_FLASH_DURATION_MS,
  type PresentHighlightPhase,
} from "#utils/taskHelpers"
import useIsSuperView from "#views/SuperView/useIsSuperView"

import AddStudentTaskForm from "./AddStudentTaskForm"

const CARD_BORDER_RADIUS = 12
const CARD_WHITE = "#FFFFFF"
const FLASH_RED = "#FFCDD2"
const GLOW_YELLOW = "#FFFDE7"
const NO_RING = "0 0 0 0 rgba(255, 193, 7, 0)"

/**
 * JARVIS-69 attention cue by phase. Flash: quick red pulses while the student
 * is walking in; glow: a soft yellow pulse that stays until the card closes.
 * Header urgency colours live on the clock button, so the ring + background
 * never compete with them.
 */
const HIGHLIGHT_ANIMATION: Record<
  PresentHighlightPhase | "none",
  TargetAndTransition
> = {
  flash: {
    backgroundColor: [CARD_WHITE, FLASH_RED, CARD_WHITE],
    boxShadow: [NO_RING, "0 0 0 5px rgba(244, 67, 54, 0.85)", NO_RING],
    transition: { duration: 1, ease: "easeInOut", repeat: Infinity },
  },
  glow: {
    backgroundColor: GLOW_YELLOW,
    boxShadow: [NO_RING, "0 0 0 6px rgba(255, 193, 7, 0.55)", NO_RING],
    transition: { duration: 1.6, ease: "easeInOut", repeat: Infinity },
  },
  none: {
    backgroundColor: CARD_WHITE,
    boxShadow: NO_RING,
    transition: { duration: 0.3 },
  },
}

/**
 * Turns a "marked present at" timestamp into the current highlight phase and
 * flips flash -> glow once the flash window elapses. A new timestamp (student
 * re-marked present) restarts the flash.
 */
const usePresentHighlightPhase = (
  markedPresentAt: number | undefined
): PresentHighlightPhase | null => {
  const [phase, setPhase] = useState<PresentHighlightPhase | null>(() =>
    markedPresentAt === undefined
      ? null
      : getPresentHighlightPhase(markedPresentAt, Date.now())
  )

  useEffect(() => {
    if (markedPresentAt === undefined) {
      setPhase(null)
      return
    }

    const initialPhase = getPresentHighlightPhase(markedPresentAt, Date.now())
    setPhase(initialPhase)
    if (initialPhase === "glow") return

    const remainingMs = markedPresentAt + PRESENT_FLASH_DURATION_MS - Date.now()
    const timeout = setTimeout(() => setPhase("glow"), remainingMs)
    return () => clearTimeout(timeout)
  }, [markedPresentAt])

  return phase
}

const SHeader = styled(Flex)<{ isCompleted: boolean; isSuperView: boolean }>`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${({ isSuperView }) => (isSuperView ? "4px 12px" : "12px")};
  color: ${({ isCompleted }) => (isCompleted ? COLORS.GRAY5 : "default")};
`

const SButton = styled(Button)`
  transition: all 0.3s ease-in-out;
`

const StudentTaskCardContent = ({
  label,
  onClick,
  isCompleted,
}: {
  label: string
  onClick: () => void
  isCompleted: boolean
}) => {
  const isSuperView = useIsSuperView()
  const theme = useThemeContext()
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      whileHover={{ textDecoration: "underline" }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      onClick={onClick}
      style={{
        cursor: "pointer",
        width: "fit-content",
        padding: isSuperView ? "2px 8px" : "4px 8px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: isCompleted ? theme.disabled : theme.black,
        fontSize: isSuperView ? "10px" : "12px",
      }}
    >
      <Checkbox checked={isCompleted} disabled={isCompleted} />
      {label}
    </motion.div>
  )
}

const TaskItemTooltipContent = ({
  isUrgent,
  tasks,
}: {
  isUrgent: boolean
  tasks: StudentTask[]
}) => {
  const theme = useThemeContext()

  return (
    <Flex col gap={6} style={{ padding: 4 }}>
      <div>{isUrgent ? "Urgent" : "Not urgent"}</div>
      <Flex col gap={6}>
        {tasks.map((task, index) => (
          <Flex key={`task-item-${task.createdAt}-${index}`} col>
            <Flex style={{ fontSize: 12, color: theme.white }}>
              Created by {task.createdBy}
            </Flex>
            <Flex style={{ fontSize: 10, color: theme.disabled }}>
              {task.createdAt}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  )
}

const TaskListCard = ({
  children,
  studentId,
  highlightPhase,
}: {
  children: ReactChild
  studentId: string
  highlightPhase: PresentHighlightPhase | null
}) => {
  const isSuperView = useIsSuperView()

  return (
    <motion.div
      data-testid={`student-task-card-${studentId}`}
      data-present-highlight={highlightPhase ?? "none"}
      animate={HIGHLIGHT_ANIMATION[highlightPhase ?? "none"]}
      style={{
        borderRadius: CARD_BORDER_RADIUS,
        // Margin pushes card so the border is visible
        marginLeft: 1,
        width: "fit-content",
      }}
    >
      <Card
        // Use style to override the Card defaults
        style={{
          width: isSuperView ? 370 : 322,
          height: "fit-content",
          padding: 0,
          justifyContent: "space-between",
          borderRadius: CARD_BORDER_RADIUS,
          // The animated wrapper owns the background so the flash shows through
          background: "transparent",
        }}
      >
        {children}
      </Card>
    </motion.div>
  )
}

const StudentTaskCard = ({ item }: { item: StudentTaskCardData }) => {
  const isSuperView = useIsSuperView()

  const { studentId, header, isUrgent, tasks, isAllTasksCompleted } = item
  const { border, white, gray } = useThemeContext()

  const { presentHighlights } = useAdminViewContext()
  const highlightPhase = usePresentHighlightPhase(presentHighlights[studentId])

  const { toggleTask } = useToggleStudentTask(studentId)

  const getBackgroundColor = (isUrgent: boolean, isCompleted: boolean) => {
    if (isUrgent && isCompleted) {
      return COLORS.priority.URGENT_DONE
    }
    if (isUrgent) {
      return COLORS.priority.URGENT
    }
    return isCompleted ? gray[1] : gray[3]
  }

  return (
    <TaskListCard studentId={studentId} highlightPhase={highlightPhase}>
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -10 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <SHeader isCompleted={isAllTasksCompleted} isSuperView={isSuperView}>
          <h3 style={{ fontSize: isSuperView ? "12px" : "14px" }}>{header}</h3>
          <Flex gap={6}>
            <AddStudentTaskForm studentId={studentId} showLabel={false} />
            <Tooltip
              title={
                <TaskItemTooltipContent isUrgent={isUrgent} tasks={tasks} />
              }
              placement="top"
            >
              <SButton
                icon={<ClockCircleOutlined />}
                style={{
                  backgroundColor: getBackgroundColor(
                    isUrgent,
                    isAllTasksCompleted
                  ),
                  color: white,
                }}
                size={isSuperView ? "small" : undefined}
              />
            </Tooltip>
          </Flex>
        </SHeader>
      </motion.div>
      <Flex
        col
        gap={2}
        style={{
          padding: isSuperView ? "4px 6px" : "12px",
          borderTop: `2px solid ${border}`,
        }}
      >
        {tasks.map((task) => (
          <StudentTaskCardContent
            key={task.id}
            {...task}
            onClick={() => toggleTask(task.id)}
          />
        ))}
      </Flex>
    </TaskListCard>
  )
}

export default StudentTaskCard
